import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, Calendar as CalIcon, Check, ShoppingBag } from "lucide-react";
import { siteImages } from "@/lib/siteData";
import { toast } from "sonner";

type Product = {
  id: string;
  name: string;
  slug: string;
  price: number;
  images: unknown;
  category: string | null;
  description: string | null;
  short_description: string | null;
  body: string | null;
  seo_title: string | null;
  seo_description: string | null;
  og_image: string | null;
  bookable_studio_id: string | null;
  min_hours: number;
};

type Variant = {
  id: string;
  product_id: string;
  size: string | null;
  price: number | null;
  sku: string | null;
  active: boolean;
};

type Studio = { id: string; slug: string; name: string };

const fmt = (n: number) => `$${n.toFixed(2)}`;

const imagesOf = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((x) => (typeof x === "string" ? x : (x as { url?: string })?.url))
    .filter((u): u is string => typeof u === "string" && u.length > 0);
};

// Try to derive hours from variant label e.g. "30 minutes", "60 min", "90 min · ...", "Basic — 30 min · …"
const hoursFromLabel = (label: string | null, fallback: number): number => {
  if (!label) return fallback;
  const m = /(\d+)\s*(min|minutes|hr|hour|hours|h)\b/i.exec(label);
  if (!m) return fallback;
  const n = parseInt(m[1], 10);
  const unit = m[2].toLowerCase();
  if (unit.startsWith("h")) return Math.max(1, n);
  // minutes → round up to whole hour for calendar slot
  return Math.max(1, Math.ceil(n / 60));
};

const ShopProductPage = () => {
  const { slug = "" } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [variants, setVariants] = useState<Variant[]>([]);
  const [studio, setStudio] = useState<Studio | null>(null);
  const [variantId, setVariantId] = useState<string>("");
  const [imgIdx, setImgIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data: p } = await supabase
        .from("products")
        .select("id,name,slug,price,images,category,description,short_description,body,seo_title,seo_description,og_image,bookable_studio_id,min_hours")
        .eq("slug", slug)
        .eq("active", true)
        .maybeSingle();
      if (cancelled) return;
      if (!p) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setProduct(p as Product);

      const [{ data: v }, studioRes] = await Promise.all([
        supabase
          .from("product_variants")
          .select("id,product_id,size,price,sku,active")
          .eq("product_id", p.id)
          .eq("active", true)
          .order("price", { ascending: true }),
        p.bookable_studio_id
          ? supabase.from("studios").select("id,slug,name").eq("id", p.bookable_studio_id).maybeSingle()
          : Promise.resolve({ data: null }),
      ]);
      if (cancelled) return;
      const vs = (v ?? []) as Variant[];
      setVariants(vs);
      if (vs.length > 0) setVariantId(vs[0].id);
      setStudio((studioRes.data as Studio | null) ?? null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const selectedVariant = useMemo(
    () => variants.find((v) => v.id === variantId) ?? null,
    [variants, variantId]
  );

  const displayPrice = useMemo(() => {
    if (selectedVariant?.price != null) return Number(selectedVariant.price);
    return product ? Number(product.price) : 0;
  }, [selectedVariant, product]);

  const imgs = product ? imagesOf(product.images) : [];
  const heroImg = imgs[imgIdx] ?? siteImages.consultationImage;

  // SEO
  useEffect(() => {
    if (!product) return;
    const title = product.seo_title || `${product.name} | MyStudio`;
    document.title = title;
    const ensure = (selector: string, create: () => HTMLElement) => {
      let el = document.head.querySelector(selector) as HTMLElement | null;
      if (!el) { el = create(); document.head.appendChild(el); }
      return el;
    };
    const desc = product.seo_description || product.short_description || product.description || "";
    const setMeta = (name: string, content: string) => {
      const el = ensure(`meta[name="${name}"]`, () => {
        const m = document.createElement("meta"); m.setAttribute("name", name); return m;
      });
      el.setAttribute("content", content);
    };
    const setOg = (prop: string, content: string) => {
      const el = ensure(`meta[property="${prop}"]`, () => {
        const m = document.createElement("meta"); m.setAttribute("property", prop); return m;
      });
      el.setAttribute("content", content);
    };
    setMeta("description", desc);
    setOg("og:title", title);
    setOg("og:description", desc);
    setOg("og:type", "product");
    if (product.og_image || imgs[0]) setOg("og:image", product.og_image || imgs[0]);
    // canonical
    const canonical = ensure(`link[rel="canonical"]`, () => {
      const l = document.createElement("link"); l.setAttribute("rel", "canonical"); return l;
    });
    canonical.setAttribute("href", `${window.location.origin}/shop/${product.slug}`);

    // JSON-LD
    const ld = {
      "@context": "https://schema.org/",
      "@type": "Product",
      name: product.name,
      image: imgs.length ? imgs : undefined,
      description: desc,
      sku: product.slug,
      brand: { "@type": "Brand", name: "MyStudio" },
      offers: variants.length
        ? variants.map((v) => ({
            "@type": "Offer",
            sku: v.sku ?? undefined,
            name: v.size ?? undefined,
            price: Number(v.price ?? product.price).toFixed(2),
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            url: `${window.location.origin}/shop/${product.slug}`,
          }))
        : {
            "@type": "Offer",
            price: Number(product.price).toFixed(2),
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            url: `${window.location.origin}/shop/${product.slug}`,
          },
    };
    const ldEl = ensure(`script[data-product-jsonld="1"]`, () => {
      const s = document.createElement("script");
      s.setAttribute("type", "application/ld+json");
      s.setAttribute("data-product-jsonld", "1");
      return s;
    });
    ldEl.textContent = JSON.stringify(ld);

    return () => {
      // Cleanup JSON-LD on unmount so other pages aren't polluted
      const stale = document.head.querySelector(`script[data-product-jsonld="1"]`);
      stale?.remove();
    };
  }, [product, variants, imgs]);

  const handleBook = () => {
    if (!product) return;
    if (product.bookable_studio_id && studio) {
      const hours = hoursFromLabel(selectedVariant?.size ?? null, product.min_hours || 1);
      const params = new URLSearchParams({
        studio: studio.slug,
        product: product.id,
        duration: String(hours),
      });
      if (selectedVariant) params.set("variant", selectedVariant.id);
      navigate(`/book?${params.toString()}`);
    } else {
      // Non-bookable: still send to /book to capture date/time, but no studio prefill
      navigate("/book");
    }
  };

  const handleBuyNow = async () => {
    if (!product) return;
    const email = window.prompt("Enter your email for the order confirmation:");
    if (!email) return;
    const promo_code = window.prompt("Promo code? (leave blank to skip)") || undefined;
    setBuying(true);
    try {
      const { data, error } = await supabase.functions.invoke("stripe-checkout-order", {
        body: {
          items: [{ product_id: product.id, variant_id: selectedVariant?.id, quantity: 1 }],
          customer: { email },
          success_url: `${window.location.origin}/shop/success`,
          cancel_url: `${window.location.origin}/shop/${product.slug}`,
          promo_code,
          attribution: (await import("@/lib/attribution")).getAttribution(),
        },
      });
      if (error) throw error;
      const url = (data as { url?: string; error?: string })?.url;
      if (!url) throw new Error((data as any)?.error ?? "Checkout failed");
      window.location.href = url;
    } catch (e) {
      toast.error((e as Error).message);
      setBuying(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-32 min-h-[50vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div className="pt-32 min-h-[50vh] container mx-auto px-4 text-center">
        <h1 className="font-display text-2xl tracking-widest mb-4">PRODUCT NOT FOUND</h1>
        <Button asChild variant="outline"><Link to="/shop"><ArrowLeft className="w-4 h-4 mr-2" /> Back to shop</Link></Button>
      </div>
    );
  }

  return (
    <div className="pt-20">
      <section className="container mx-auto px-4 py-10 md:py-16">
        <Link to="/shop" className="inline-flex items-center text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-primary mb-6">
          <ArrowLeft className="w-3 h-3 mr-1" /> Shop
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Image gallery */}
          <div>
            <div className="aspect-square bg-panel border border-border overflow-hidden">
              <img
                src={heroImg}
                alt={product.name}
                width={1024}
                height={1024}
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
            {imgs.length > 1 && (
              <div className="grid grid-cols-5 gap-2 mt-3">
                {imgs.map((u, i) => (
                  <button
                    key={u}
                    onClick={() => setImgIdx(i)}
                    className={`aspect-square overflow-hidden border ${i === imgIdx ? "border-primary" : "border-border hover:border-primary/50"}`}
                  >
                    <img src={u} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Detail */}
          <div>
            {product.category && (
              <p className="text-xs font-mono uppercase tracking-[0.3em] text-primary mb-3">{product.category}</p>
            )}
            <h1 className="text-3xl md:text-4xl font-display font-extrabold uppercase tracking-widest mb-3">{product.name}</h1>
            {product.short_description && (
              <p className="text-muted-foreground font-body mb-6">{product.short_description}</p>
            )}

            <div className="flex items-baseline gap-3 mb-6">
              <span className="text-3xl font-display text-primary">{fmt(displayPrice)}</span>
              {selectedVariant?.size && (
                <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{selectedVariant.size}</span>
              )}
            </div>

            {variants.length > 0 && (
              <div className="mb-6">
                <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Choose tier</p>
                <div className="space-y-2">
                  {variants.map((v) => {
                    const sel = v.id === variantId;
                    return (
                      <button
                        key={v.id}
                        onClick={() => setVariantId(v.id)}
                        className={`w-full text-left border p-4 transition-colors flex items-center justify-between gap-3 ${sel ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
                      >
                        <span className="flex items-center gap-2 font-body text-sm">
                          {sel && <Check className="w-4 h-4 text-primary" />}
                          {v.size ?? "Standard"}
                        </span>
                        <span className="font-display text-primary">{fmt(Number(v.price ?? product.price))}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {product.bookable_studio_id ? (
              <Button
                onClick={handleBook}
                size="lg"
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-display tracking-widest"
              >
                <CalIcon className="w-4 h-4 mr-2" />
                Book this session
              </Button>
            ) : (
              <Button
                onClick={handleBuyNow}
                disabled={buying}
                size="lg"
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-display tracking-widest"
              >
                {buying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShoppingBag className="w-4 h-4 mr-2" />}
                Buy now
              </Button>
            )}

            {studio && (
              <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mt-3">
                Studio: <Link to={`/studios/${studio.slug}`} className="text-primary hover:underline">{studio.name}</Link>
              </p>
            )}

            {product.body && (
              <div className="mt-10 pt-8 border-t border-border">
                <h2 className="text-sm font-mono uppercase tracking-widest text-muted-foreground mb-4">About this session</h2>
                <p className="font-body text-sm leading-relaxed whitespace-pre-wrap">{product.body}</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShopProductPage;
